import React from "react";
import type { TFunction } from "i18next";
import { BenefitRow, PaidPlanKey, PeriodCode, PricesIndex, TariffPlanVm } from "../../interface";
import { Currency, PlanPriceProxy, TariffPlan } from "@me-team/host/main/graphql/types";
import { amountFieldMap, currencySymbolMap, savingsFieldMap } from "./constants";

const parsePlanKey = (planName: TariffPlan): PaidPlanKey | null => {
    const prefix = String(planName).split("_")[0];
    if (prefix === "LITE") return "LITE";
    if (prefix === "PREMIUM") return "PREMIUM";
    return null;
};

const parsePeriod = (planName: TariffPlan): PeriodCode => {
    const suffix = String(planName).split("_")[1];
    return suffix === "YEARLY" ? "YEARLY" : "MONTHLY";
};

export const buildPricesIndex = (planPrices: PlanPriceProxy[] | undefined | null): PricesIndex => {
    const index: PricesIndex = {};
    if (!Array.isArray(planPrices)) return index;

    planPrices.forEach((dto) => {
        const planKey = parsePlanKey(dto.planName);
        if (!planKey) return;
        const period = parsePeriod(dto.planName);

        index[planKey] = index[planKey] ?? {};
        index[planKey]![period] = dto;
    });
    return index;
};

const formatMoney = (amount: number, currencyCode: Currency): string => {
    const symbol = currencySymbolMap[currencyCode];
    const normalized = Number.isFinite(amount) ? amount : 0;
    const formatted = normalized === 0 ? "0" : normalized.toFixed(2);
    return `${symbol}${formatted}`;
};

const getAmountByCurrency = (dto: PlanPriceProxy, currencyCode: Currency): number => {
    const field = amountFieldMap[currencyCode];
    return Number(dto[field] ?? 0);
};

const getSavingsByCurrency = (dto: PlanPriceProxy, currencyCode: Currency): number => {
    const field = savingsFieldMap[currencyCode];
    return Number(dto[field] ?? 0);
};

export const getBenefitsRows = (t: TFunction): BenefitRow[] => [
    { id: "branches", label: t("Branches") },
    { id: "users", label: t("Users") },
    { id: "services", label: t("Services") },
    { id: "logbook", label: t("Logbook") },
    { id: "customers", label: t("Customers") },
    { id: "bookingStatistics", label: t("Booking Statistics") },
    { id: "warehouseAccounting", label: t("Warehouse Accounting") },
    { id: "onlineBookingAdsFree", label: t("Online Booking Ads-Free"), isEmphasis: true },
];

const checkIcon = <i className="bi bi-check-lg"></i>;

const buildPlanRows = (t: TFunction, planKey: "STARTER" | "LITE" | "PREMIUM") => {
    if (planKey === "PREMIUM") {
        return [
            { id: "branches", value: <span className="fw-bold">{t("Unlimited")}</span> },
            { id: "users", value: <span className="fw-bold">{t("Unlimited Employees")}</span> },
            { id: "services", value: checkIcon },
            { id: "logbook", value: checkIcon },
            { id: "customers", value: checkIcon },
            { id: "bookingStatistics", value: checkIcon },
            { id: "warehouseAccounting", value: checkIcon },
            {
                id: "onlineBookingAdsFree",
                value: <span className="fw-bold">{t("Ads-Free")}</span>,
                isEmphasis: true,
            },
        ];
    }

    const lastValue =
        planKey === "STARTER" ? (
            <span className="fw-bold">{t("With Ads")}</span>
        ) : (
            <span className="fw-bold">{t("Ads-Free")}</span>
        );

    return [
        { id: "branches", value: "2" },
        { id: "users", value: t("1 Owner + 6 Employees") },
        { id: "services", value: checkIcon },
        { id: "logbook", value: checkIcon },
        { id: "customers", value: checkIcon },
        { id: "bookingStatistics", value: checkIcon },
        { id: "warehouseAccounting", value: checkIcon },
        { id: "onlineBookingAdsFree", value: lastValue, isEmphasis: true },
    ];
};

export const buildTariffPlansViewModel = (params: {
    t: TFunction;
    planPrices: PlanPriceProxy[] | null;
    currencyCode: Currency;
    period: PeriodCode;
    currentPlanKey?: "STARTER" | "LITE" | "PREMIUM" | null;
}): TariffPlanVm[] => {
    const { t, planPrices, currencyCode, period, currentPlanKey } = params;

    const pricesIndex = buildPricesIndex(planPrices);
    const currencySymbol = currencySymbolMap[currencyCode];

    const hasPaidPlanActive = currentPlanKey === "LITE" || currentPlanKey === "PREMIUM";
    const benefitsRows = getBenefitsRows(t);

    const buildPaidPlanVm = (planKey: "LITE" | "PREMIUM"): TariffPlanVm => {
        const dto = pricesIndex[planKey]?.[period];
        const isCurrent = currentPlanKey === planKey;
        const billingAmount = dto ? getAmountByCurrency(dto, currencyCode) : 0;
        const displayAmount = period === "YEARLY" ? billingAmount / 12 : billingAmount;
        const savingsAmount = dto && period === "YEARLY" ? getSavingsByCurrency(dto, currencyCode) : 0;
        const billedText = period === "YEARLY" ? t("Billed Yearly") : t("Billed Monthly");

        const saveText =
            period === "YEARLY" && savingsAmount > 0 ? (
                <>
                    {t("You save")} <strong>{formatMoney(savingsAmount, currencyCode)}</strong> / {t("year")}
                </>
            ) : undefined;

        const actionText = isCurrent ? t("Current Plan") : hasPaidPlanActive ? t("Change Plan") : t("Upgrade");

        const planRows = buildPlanRows(t, planKey);

        return {
            key: planKey,
            title: t(planKey === "LITE" ? "Lite" : "Premium"),
            displayPriceText: formatMoney(displayAmount, currencyCode),
            periodSuffixText: ` / ${t("Month")}`,
            billedText,
            saveText,
            rows: benefitsRows.map((row) => {
                const planRow = planRows.find((r) => r.id === row.id);
                return {
                    id: row.id,
                    label: row.label,
                    value: planRow?.value ?? "",
                    isEmphasis: row.isEmphasis,
                };
            }),
            isCurrent,
            isPremium: planKey === "PREMIUM",
            planId: dto?.planId,
            paymentAmount: billingAmount,
            currencyCode,
            currencySymbol,
            action: {
                text: actionText,
                variant: planKey === "PREMIUM" ? "primary" : "outline-primary",
                disabled: isCurrent,
            },
        };
    };

    const starterIsCurrent = currentPlanKey === "STARTER" || !currentPlanKey;

    const starterPlanRows = buildPlanRows(t, "STARTER");

    const starterVm: TariffPlanVm = {
        key: "STARTER",
        title: t("Starter"),
        displayPriceText: `${currencySymbol}0`,
        periodSuffixText: ` / ${t("Month")}`,
        billedText: t("Forever Free"),
        saveText: undefined,

        rows: benefitsRows.map((row) => {
            const planRow = starterPlanRows.find((r) => r.id === row.id);
            return {
                id: row.id,
                label: row.label,
                value: planRow?.value ?? "",
                isEmphasis: row.isEmphasis,
            };
        }),
        isCurrent: starterIsCurrent,
        isPremium: false,
        currencyCode,
        currencySymbol,
        hideAction: hasPaidPlanActive,
        action: starterIsCurrent
            ? { text: t("Current Plan"), variant: "outline-primary", disabled: true }
            : { text: t("Get Free"), variant: "outline-primary" },
    };

    return [starterVm, buildPaidPlanVm("LITE"), buildPaidPlanVm("PREMIUM")];
};
